import { useFormContainerContext } from '@if/mui'
import {
  AprCheckResult,
  BaseWorksheetFormConfig,
  useAprCheck,
  WORKSHEET_INPUT_DEBOUNCE_MS,
} from '@src/components/WorksheetForm'
import { useComputePersonalLoanFunding } from '@src/data/api/personal-loan-worksheet/personal-loan-worksheet-api'
import { useProvinceConfigurations } from '@src/data/selectors/app-config-selectors'
import { FundingComputedDto } from '@src/data/types/FundingComputedDto'
import { FundingInfoDto } from '@src/data/types/FundingInfoDto'
import { getEmptyComputedFundingDto } from '@src/containers/ViewWorksheet/viewWorksheet-selectors'
import { reportErrorToConsole } from '@src/services/error-logger'
import { debounce } from 'lodash-es'
import { nanoid } from 'nanoid'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Control, useWatch } from 'react-hook-form'
import { PersonalEditValues } from './personalEditForm'

export interface UsePersonalLoanLiveComputeArgs {
  control: Control<PersonalEditValues>
  config: BaseWorksheetFormConfig
  interestRate: number
  insuranceCompanyId: string | null
}

export interface PersonalLoanLiveComputeResult {
  computedInfo: FundingComputedDto
  isComputing: boolean
  aprCheck: AprCheckResult
}

// Live funding recompute for the edit page: watch the pricing inputs, debounce, abort the
// in-flight call, and hold the form container busy from the very first change so Save
// disables before the debounce settles. Must run inside FormContainer + BaseWorksheetForm.
export const usePersonalLoanLiveCompute = ({
  control,
  config,
  interestRate,
  insuranceCompanyId,
}: UsePersonalLoanLiveComputeArgs): PersonalLoanLiveComputeResult => {
  // Only the container's busy flag comes from context; form state arrives as `control`.
  const { setBusy } = useFormContainerContext<PersonalEditValues>()
  const [computeFunding, isMutating] = useComputePersonalLoanFunding()
  const provinceConfigs = useProvinceConfigurations()

  const [computedInfo, setComputedInfo] = useState<FundingComputedDto>(getEmptyComputedFundingDto())
  const [isComputePending, setIsComputePending] = useState(false)

  const abortRef = useRef<AbortController | null>(null)
  useEffect(() => () => abortRef.current?.abort(), [])

  const doCompute = useCallback(
    async (dto: FundingInfoDto) => {
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      try {
        const info = await computeFunding(dto)
        if (!controller.signal.aborted) setComputedInfo(info)
      } catch (error) {
        reportErrorToConsole(error)
      } finally {
        if (!controller.signal.aborted) setIsComputePending(false)
      }
    },
    [computeFunding],
  )

  const debouncedCompute = useMemo(() => debounce(doCompute, WORKSHEET_INPUT_DEBOUNCE_MS), [doCompute])
  useEffect(
    () => () => {
      debouncedCompute.cancel()
    },
    [debouncedCompute],
  )

  const [amountRequested, paymentFrequency, paymentPlanId, term, activationDate, firstPaymentDate, includeProtection] =
    useWatch({
      control,
      name: [
        'amountRequested',
        'paymentFrequency',
        'paymentPlanId',
        'term',
        'activationDate',
        'firstPaymentDate',
        'includeProtection',
      ],
    })

  useEffect(() => {
    if (
      amountRequested == null ||
      amountRequested < config.minLoanAmount ||
      !paymentFrequency ||
      !paymentPlanId ||
      term == null ||
      !activationDate ||
      !firstPaymentDate
    ) {
      setComputedInfo(getEmptyComputedFundingDto())
      setIsComputePending(false)
      return
    }

    const dto: FundingInfoDto = {
      financingProgramId: config.financingProgramId,
      amountRequested,
      includeInsurance: includeProtection,
      insuranceCompanyId,
      paymentFrequency,
      paymentPlanId,
      term,
      interestRate,
      hasCoApplicant: config.hasCoApplicant,
      stateIso: config.stateIso,
      deliveryOn: activationDate,
      firstPaymentOn: firstPaymentDate.toString(),
      requestId: nanoid(),
      creditApplicationId: config.creditApplicationId,
      merchantId: config.merchantId,
    }
    setIsComputePending(true)
    void debouncedCompute(dto)
  }, [
    amountRequested,
    paymentFrequency,
    paymentPlanId,
    term,
    activationDate,
    firstPaymentDate,
    includeProtection,
    config,
    interestRate,
    insuranceCompanyId,
    debouncedCompute,
  ])

  const isComputing = isComputePending || isMutating
  useEffect(() => {
    setBusy('computeFunding', isComputing)
    return () => setBusy('computeFunding', false)
  }, [setBusy, isComputing])

  const aprCheck = useAprCheck(computedInfo, config.stateIso, config.effectiveRateThreshold, provinceConfigs)

  return { computedInfo, isComputing, aprCheck }
}
